import Fuse from 'fuse.js';

export interface SearchIndex<T> {
  search(pattern: string): T[];
}

export const createStrictSearchIndex = <T>(list: T[], fields: string[], options: any = {}): SearchIndex<T> => {
  return createSearchIndex(list, fields, { ...options, threshold: 0 });
};

export const createSearchIndex = <T>(list: T[], fields: string[], options: any = {}): SearchIndex<T> => {
  const defaultOptions = {
    keys: [...fields],
    shouldSort: true,
    tokenize: true,
    matchAllTokens: true,
    threshold: 0.1,
    distance: 100,
    maxPatternLength: 32,
    minMatchCharLength: 1,
  };

  const index = new Fuse(list, { ...defaultOptions, ...options });

  return {
    search: (pattern: string) => {
      return index.search(pattern).map((i) => i.item);
    },
  };
};

export interface DataUrlInfo {
  content: string;
  contentType: string;
}

export const toDataUrlInfo = (dataURL: string): DataUrlInfo => {
  const BASE64_MARKER = ';base64,';

  const parts = dataURL.indexOf(BASE64_MARKER) > -1 ? dataURL.split(BASE64_MARKER) : dataURL.split(',');

  const contentType = parts[0].split(':')[1];
  const content = parts[1];

  return { content, contentType };
};

export const dataURLToBlob = (dataURL: string) => {
  const info = toDataUrlInfo(dataURL);
  const raw = window.atob(info.content);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: info.contentType });
};

export const formatFileSize = (bytes?: number): string => {
  const megabytes = (bytes ?? 0) / (1024 * 1024);
  if (megabytes > 0.1) {
    return `${megabytes.toFixed(1)} MB`;
  }
  return `${((bytes ?? 0) / 1024).toFixed(1)} KB`;
};

export const blobToDataUrl = (blob: Blob | File): Promise<string> => {
  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);

    reader.onloadend = () => {
      const url = reader.result as string;
      resolve(url);
    };
  });
};
